/**
 * /Recon portal — Trusted recipients page (Bucket Q2.5).
 *
 * Buyer's master allowlist for bank-mode shares. Adding entries here
 * lets ANY share with match_mode='bank' validate against this list at
 * the recipient's /validate step. Live binding: changes ripple to
 * every existing bank-mode token instantly. There's no per-share
 * pinning — that's deliberate, the whole point of the bank.
 *
 * Two sections: emails (specific people) + domains (whole companies).
 * Bulk-add textarea so the buyer can paste a vendor list.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  listTrustEntriesApiV1ClientPortalTrustEntriesGet,
  addTrustEntryApiV1ClientPortalTrustEntriesPost,
  archiveTrustEntryApiV1ClientPortalTrustEntriesEntryIdDelete,
} from "@hackereyes/api-client";
import "./trusted-recipients.css";

interface TrustEntry {
  id: string;
  entry_type: "email" | "domain";
  entry_value: string;
  label: string | null;
  archived_at: string | null;
  created_at: string;
}

interface TrustEntryListResponse {
  entries: TrustEntry[];
  total_active: number;
}

export function TrustedRecipients() {
  const queryClient = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ["trust-entries", showArchived],
    queryFn: async () => {
      const { data, error } = await listTrustEntriesApiV1ClientPortalTrustEntriesGet({
        query: { include_archived: showArchived },
      });
      if (error) throw new Error("load_failed");
      return data as unknown as TrustEntryListResponse;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (entry: {
      entry_type: "email" | "domain";
      entry_value: string;
      label?: string;
    }) => {
      const { data, error } = await addTrustEntryApiV1ClientPortalTrustEntriesPost({
        body: entry as never,
      });
      if (error) {
        const detail = (error as Record<string, unknown>)?.detail as string;
        throw new Error(detail || "add_failed");
      }
      return data;
    },
    onSuccess: () => {
      setErrorMsg(null);
      queryClient.invalidateQueries({ queryKey: ["trust-entries"] });
    },
    onError: (err: Error) => {
      setErrorMsg(
        err.message.includes("already in your trust bank")
          ? "That entry is already in your trust bank."
          : err.message,
      );
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await archiveTrustEntryApiV1ClientPortalTrustEntriesEntryIdDelete({
        path: { entry_id: entryId },
      });
      if (error) throw new Error("archive_failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trust-entries"] });
    },
  });

  const entries = listQuery.data?.entries ?? [];
  const activeEntries = entries.filter((e) => e.archived_at === null);
  const archivedEntries = entries.filter((e) => e.archived_at !== null);
  const emails = activeEntries.filter((e) => e.entry_type === "email");
  const domains = activeEntries.filter((e) => e.entry_type === "domain");

  return (
    <div className="trusted-page">
      <div className="trusted-inner">
        <div className="trusted-header">
          <div className="eyebrow">Account · Trusted recipients</div>
          <h1 className="trusted-title">Trusted recipients</h1>
          <p className="trusted-lede">
            Anyone whose email matches an entry below can validate a
            "Trust Bank" share token and view the deliverable. Adding
            and archiving entries takes effect on the next validation
            attempt — every existing share that uses bank mode picks
            up these changes instantly.
          </p>
        </div>

        {errorMsg && <div className="trusted-error">{errorMsg}</div>}

        <AddForm
          onAdd={(entry) => addMutation.mutate(entry)}
          submitting={addMutation.isPending}
        />

        <Section
          title="Trusted domains"
          description="Anyone with an email at one of these domains can validate. Subdomains are included automatically (e.g., procurement.acme.com matches an entry of acme.com)."
          entries={domains}
          onArchive={(id) => archiveMutation.mutate(id)}
          emptyHint="No trusted domains yet. Add a vendor's domain above to start."
        />

        <Section
          title="Trusted emails"
          description="Specific addresses that can validate. Use these for one-off recipients (a board member, an attorney) when a domain rule would be too broad."
          entries={emails}
          onArchive={(id) => archiveMutation.mutate(id)}
          emptyHint="No trusted emails yet. Most TPRM use cases prefer domain entries; emails are for specific people."
        />

        <div className="trusted-footer">
          <button
            type="button"
            className="trusted-toggle-archived"
            onClick={() => setShowArchived((v) => !v)}
            aria-pressed={showArchived}
          >
            {showArchived ? "Hide archived" : "Show archived"} ({archivedEntries.length})
          </button>
          {showArchived && archivedEntries.length > 0 && (
            <div className="trusted-archived-list">
              {archivedEntries.map((e) => (
                <div key={e.id} className="trusted-archived-row">
                  <span className="trusted-archived-type">{e.entry_type}</span>
                  <span className="trusted-archived-value">{e.entry_value}</span>
                  <span className="trusted-archived-label">{e.label}</span>
                </div>
              ))}
            </div>
          )}
          <p className="trusted-footnote">
            Need help managing your share tokens?{" "}
            <Link to="/portal">View your engagements</Link> to create or
            revoke individual shares.
          </p>
        </div>
      </div>
    </div>
  );
}

function AddForm({
  onAdd,
  submitting,
}: {
  onAdd: (entry: {
    entry_type: "email" | "domain";
    entry_value: string;
    label?: string;
  }) => void;
  submitting: boolean;
}) {
  const [entryType, setEntryType] = useState<"email" | "domain">("domain");
  const [entryValue, setEntryValue] = useState("");
  const [label, setLabel] = useState("");

  return (
    <form
      className="trusted-add"
      onSubmit={(e) => {
        e.preventDefault();
        if (!entryValue.trim()) return;
        onAdd({
          entry_type: entryType,
          entry_value: entryValue.trim().toLowerCase(),
          label: label.trim() || undefined,
        });
        setEntryValue("");
        setLabel("");
      }}
    >
      <div className="trusted-add-row">
        <label className="trusted-add-type">
          <select
            value={entryType}
            onChange={(e) => setEntryType(e.target.value as "email" | "domain")}
          >
            <option value="domain">Domain</option>
            <option value="email">Email</option>
          </select>
        </label>
        <input
          className="trusted-add-value"
          type="text"
          placeholder={
            entryType === "domain"
              ? "acmecorp.com"
              : "jane@acmecorp.com"
          }
          value={entryValue}
          onChange={(e) => setEntryValue(e.target.value)}
          required
        />
        <input
          className="trusted-add-label"
          type="text"
          placeholder="Label (optional)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <button
          type="submit"
          className="recon-btn recon-btn-primary"
          disabled={submitting || !entryValue.trim()}
        >
          Add
        </button>
      </div>
    </form>
  );
}

function Section({
  title,
  description,
  entries,
  onArchive,
  emptyHint,
}: {
  title: string;
  description: string;
  entries: TrustEntry[];
  onArchive: (id: string) => void;
  emptyHint: string;
}) {
  return (
    <section className="trusted-section">
      <h2 className="trusted-section-title">{title}</h2>
      <p className="trusted-section-lede">{description}</p>
      {entries.length === 0 ? (
        <div className="trusted-empty">{emptyHint}</div>
      ) : (
        <ul className="trusted-list">
          {entries.map((e) => (
            <li key={e.id} className="trusted-row">
              <span className="trusted-row-value">{e.entry_value}</span>
              {e.label && <span className="trusted-row-label">{e.label}</span>}
              <span className="trusted-row-added">
                Added {new Date(e.created_at).toLocaleDateString()}
              </span>
              <button
                type="button"
                className="trusted-row-archive"
                onClick={() => {
                  if (confirm(`Archive ${e.entry_value}? You can re-add later.`)) {
                    onArchive(e.id);
                  }
                }}
              >
                Archive
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
